import React, { useEffect, useState } from "react";
import {
    ConfirmDialog,
    createValidationResult,
    FormValidationSummary,
    useNotifications,
    WarningMessage
} from "sprint-common-web";

import { AssetContentValidationFailure } from "../../../../../../../../common/api/master-api/assets/bulk-content/ValidateBulkCloudAssetContentResponse";
import {
    ContentReference,
    resolveContentTitle,
    resolveContentVersion
} from "../../../../../../../../common/types/ContentReference";
import { BulkAssetContentOperationList } from "../../../../../../common/bulk-content-operations/BulkAssetContentOperationList";
import { useConformingCloudAssets } from "../../../../../common/hooks/useConformingCloudAssets";
import { mapToBulkEditCloudAssetContentItem } from "./BulkEditCloudAssetContentItem";
import { useBulkEditCloudAssetContent } from "./useBulkEditCloudAssetContent";

export interface BulkEditCloudAssetContentConfirmDialogProps {
    assetId: string;
    contentReference: ContentReference;
    isOpen: boolean;
    onClose: () => void;
    onHandleConfirmation?: () => void;
}

export const BulkEditCloudAssetContentConfirmDialog = ({
    assetId,
    contentReference,
    isOpen,
    onHandleConfirmation,
    onClose
}: BulkEditCloudAssetContentConfirmDialogProps): JSX.Element => {
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isValidationSuccessful, setIsValidationSuccessful] = useState(false);
    const [assetFailures, setAssetFailures] = useState<AssetContentValidationFailure[]>([]);
    const [generalFailures, setGeneralFailures] = useState<ReturnType<typeof createValidationResult> | undefined>(
        undefined
    );

    const { data: operations, isLoading: isLoadingOperations } = useConformingCloudAssets(assetId);
    const { validateBulkCloudAssetContent, updateBulkCloudAssetContent } = useBulkEditCloudAssetContent({
        invalidationKey: assetId
    });
    const { success, error } = useNotifications();

    useEffect(() => {
        if (isOpen) {
            void handleValidation();
        }
    }, [isOpen]);

    const handleValidation = async () => {
        setIsValidating(true);

        try {
            const response = await validateBulkCloudAssetContent.mutateAsync({ assetId, contentReference });

            setIsValidationSuccessful(response.result.isValid);
            setAssetFailures(response.assetFailures);
            setGeneralFailures(response.assetFailures.length ? undefined : response.result);
        } finally {
            setIsValidating(false);
        }
    };

    const handleConfirmation = async () => {
        await updateBulkCloudAssetContent.mutateAsync({ assetId, contentReference });

        setIsConfirmed(true);
        onHandleConfirmation?.();
    };

    const handleConfirmationWithNotification = async () => {
        try {
            await handleConfirmation();
            success("Assets have been moved to the target content version successfully.");
        } catch (e) {
            error("Something went wrong while updating asset group content.");
        }
    };

    const targetContentTitle = resolveContentTitle(contentReference);
    const targetVersions = resolveContentVersion(contentReference);
    const operationItems = (operations ?? []).map((item) => ({
        ...mapToBulkEditCloudAssetContentItem(item),
        targetContentTitle,
        targetVersions
    }));

    return (
        <ConfirmDialog
            width={1200}
            title="Bulk edit content"
            text={"Are you sure you want to move all assets in this group to the target content?"}
            hidden={!isOpen}
            isBlocking={isConfirmed}
            confirm={{
                onClick: handleConfirmationWithNotification,
                disabled: !isValidationSuccessful || isValidating || isConfirmed
            }}
            cancel={{ onClick: onClose, buttonText: isConfirmed ? "Close" : "Cancel" }}
        >
            <FormValidationSummary isValidating={isValidating} validationResult={generalFailures} />
            {assetFailures.map((assetFailure) => (
                <FormValidationSummary
                    key={assetFailure.assetId}
                    validationResult={createValidationResult(
                        assetFailure.failures.map((failure) => ({
                            ...failure,
                            message: `${assetFailure.filepath}: ${failure.message}`
                        }))
                    )}
                />
            ))}
            <BulkAssetContentOperationList operations={operationItems} isLoading={isLoadingOperations} />
            <WarningMessage message="New assets will appear on the listing after a while." />
        </ConfirmDialog>
    );
};
